/* eslint-disable @typescript-eslint/no-explicit-any */
import { format } from "date-fns";
import { type Schedule } from "../api/schedule";
import { formatTimeIST } from "../utils/function";
import { useState } from "react";
import { ScheduleDetailsPopup } from "./songs/SongPopUp";

interface HistoryProps {
  historyItems: Schedule[];
  isLoading: boolean;
  currentPlaying?: boolean; 
  setPlaying?: any
}

export default function History({ historyItems, isLoading, currentPlaying, setPlaying }: HistoryProps) {

  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [mp3Exists, setMp3Exists] = useState<boolean | null>(null);
  const [checkingMp3, setCheckingMp3] = useState(false);

  const handleViewDetails = async (schedule: Schedule) => {
    setSelectedSchedule(schedule);

    setCheckingMp3(true);
    setMp3Exists(null);

    try {
      const res = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/stream/schedule/${schedule.id}/exists`
      );

      const data = await res.json();
      setMp3Exists(data.exists);
    } catch {
      setMp3Exists(false);
    } finally {
      setCheckingMp3(false);
    }
  };


  const formatDisplayDate = (date: Date) => {
    return format(date, "EEEE, MMMM d, yyyy");
  };

  if (isLoading) {
    return (
      <section className="py-24 px-4 bg-linear-to-b from-black to-[#042033] min-h-screen">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="h-10 w-64 bg-white/10 rounded-lg mx-auto mb-4 animate-pulse"></div>
            <div className="h-5 w-80 bg-white/10 rounded-lg mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 animate-pulse">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-white/10"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 bg-white/10 rounded"></div>
                    <div className="h-5 w-1/2 bg-white/10 rounded"></div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-4 bg-white/10 rounded w-full"></div>
                  <div className="h-4 bg-white/10 rounded w-5/6"></div>
                  <div className="h-4 bg-white/10 rounded w-4/5"></div>
                </div>
                <div className="mt-6 h-9 bg-white/10 rounded-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 pt-32 px-4 bg-linear-to-b from-black to-[#042033] min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-white text-3xl font-medium mb-4">History</h2>
          <p className="text-white/90">Browse previously played shows and discover what you might have missed</p>
        </div>

        {/* Grid */}
        {historyItems.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 z-10">
            {historyItems.map((item) => (
              <div key={item?.id} className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col gap-6 hover:bg-white/10 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-linear-to-b from-[#174868] to-[#55afba] flex items-center justify-center shrink-0">
                    {item?.show?.thumbnail ? (
                      <img
                        src={item?.show?.thumbnail}
                        alt={item?.show?.name}
                        className="w-full h-full rounded-lg object-cover"
                      />
                    ) : (
                      <span className="text-white text-xl font-bold">
                        {item?.show?.name?.charAt(0).toUpperCase() ?? "-"}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="text-[#55afba] text-sm font-medium">
                      {formatTimeIST(item?.startTime)} - {formatTimeIST(item?.endTime)}
                    </p>
                    <h3 className="text-white font-medium">{item?.show?.name ?? "-"}</h3>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Host:</span>
                    <span className="text-white font-medium">{item?.show?.hostName ?? "-"}</span>
                  </div>
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-white/70">Type:</span>
                    <div className="flex gap-2 flex-wrap justify-end">
                      {item?.show?.categories?.slice(0, 2).map(category => (
                        <span
                          key={category?.id}
                          className="text-cyan-200 bg-cyan-900/30 border border-cyan-500/30 px-2 py-0.5 rounded text-xs"
                        >
                          {category?.name ?? "-"}
                        </span>
                      ))}
                      {item?.show?.categories?.length > 2 && (
                        <span className="text-cyan-200 bg-cyan-900/30 border border-cyan-500/30 px-2 py-0.5 rounded text-xs">
                          +{item?.show?.categories?.length - 2}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Status:</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${item?.status === 'completed'
                      ? 'bg-green-900/30 border border-green-500/30 text-green-200'
                      : item?.status === 'live'
                        ? 'bg-red-900/30 border border-red-500/30 text-red-200'
                        : 'bg-blue-900/30 border border-blue-500/30 text-blue-200'
                      }`}>
                      {item?.status ?? "-"}
                    </span>
                  </div>
                </div>

                <button
                  className="w-full py-2 border border-white/20 rounded-lg text-white text-sm font-medium hover:bg-white/10 transition-colors mt-auto cursor-pointer"
                  onClick={() => {
                    // Handle view details
                    handleViewDetails(item);
                  }}
                >
                  View Details
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-white/70">No shows found for {formatDisplayDate(new Date())}</p>
          </div>
        )}
      </div>

      {selectedSchedule && 
        <ScheduleDetailsPopup
          selectedSchedule={selectedSchedule}
          setSelectedSchedule={setSelectedSchedule}
          checkingMp3={checkingMp3}
          mp3Exists={mp3Exists}
          currentPlaying={currentPlaying}
          setPlaying={setPlaying}
        />
      }

    </section>
  );
}